"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    ANNUAIRE_REGLEMENTEES,
    PART_PATRONALE_MINIMALE,
    RISQUES_LOCAL,
    obligations,
    recapitulatif,
    valider,
} from "@/lib/tools/mandatoryInsurance";
import {
    Avancer,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    remplir,
    useParcours,
} from "./Journey";

/**
 * Les assurances obligatoires du micro-entrepreneur.
 *
 * CINQ QUESTIONS, ET AUCUNE RÉPONSE AVANT LA DERNIÈRE. Un diagnostic partiel
 * annoncerait moins d'obligations qu'il n'y en a, et laisserait quelqu'un
 * travailler sans couverture en se croyant en règle : le module rend `null`
 * tant qu'une question reste ouverte, et le parcours ne montre rien.
 *
 * LE PARCOURS NE PUBLIE AUCUNE LISTE DE PROFESSIONS. La page officielle n'en
 * donne pas de close : elle cite des exemples et renvoie à l'annuaire de l'INPI.
 * L'écran demande donc au lecteur si son activité est réglementée et lui dit où
 * le vérifier, plutôt que de lui présenter une liste dont l'absence vaudrait
 * dispense.
 *
 * TROIS OBLIGATIONS SONT OUBLIÉES PARTOUT : le véhicule utilisé à titre
 * professionnel, le local loué, la complémentaire des salariés. Elles pèsent
 * autant que les deux que tout le monde connaît.
 */

const L = {
    fr: {
        phases: ["Votre activité", "Vos moyens", "Ce qui s'impose"],
        retour: "Retour",
        prive: "Rien ne sort de cet onglet : le diagnostic se fait dans votre navigateur, et tout s'efface quand vous le fermez.",

        oui: "Oui",
        non: "Non",

        batimentTitre: "Votre activité relève-t-elle du bâtiment ?",
        batimentAide: "Construction, rénovation d'importance, extension, et tout ce qui touche à l'ouvrage lui-même. L'assurance décennale y est obligatoire : son défaut est un délit, et elle se souscrit avant le chantier.",
        batimentOuiDetail: "Maçonnerie, plaquisterie, électricité, plomberie, couverture, peinture…",
        batimentNonDetail: "Mon activité ne touche pas à la construction",

        reglementeeTitre: "Votre activité est-elle réglementée ?",
        reglementeeAide: "Une activité réglementée est celle dont l'accès ou l'exercice est encadré par un texte propre : diplôme, inscription à un ordre, agrément.",
        reglementeeOuiDetail: "Mon activité figure parmi les professions réglementées",
        reglementeeNonDetail: "Aucun texte particulier n'encadre mon métier",
        reglementeeNote: "Aucune liste close n'est publiée. Vérifiez la vôtre dans {annuaire} : c'est là que l'administration renvoie, et un métier absent d'une liste incomplète n'est pas pour autant dispensé.",

        vehiculeTitre: "Utilisez-vous un véhicule pour votre activité ?",
        vehiculeAide: "Une voiture personnelle compte aussi, dès qu'elle sert à des déplacements professionnels.",
        vehiculeOuiDetail: "Déplacements clients, livraisons, chantiers, tournées",
        vehiculeNonDetail: "Je travaille sans véhicule",

        localTitre: "Louez-vous un local professionnel ?",
        localAide: "Un local loué appelle sa propre assurance, distincte de celle de votre activité.",
        localOuiDetail: "Atelier, boutique, bureau, entrepôt",
        localNonDetail: "Je travaille chez moi ou chez mes clients",

        salariesTitre: "Employez-vous des salariés ?",
        salariesAide: "Rare en micro-entreprise, mais l'obligation existe dès le premier contrat.",
        salariesOuiDetail: "Au moins un salarié, même à temps partiel",
        salariesNonDetail: "Je travaille seul",

        resultatTitre: "Ce que vous devez assurer",
        resultatAucuneTitre: "Aucune assurance ne vous est imposée",
        recapTitre: "Le détail",
        recommencer: "Recommencer",

        decennaleTitre: "La décennale se souscrit avant le chantier",
        decennale: "La date qui compte est celle de l'ouverture du chantier, et non celle du sinistre : elle doit tomber pendant la validité du contrat. Un contrat résilié couvre donc encore les chantiers ouverts pendant qu'il courait, et un chantier ouvert sans assurance ne sera rattrapé par aucune souscription ultérieure. Seuls les travaux déclarés au contrat sont garantis. Enfin, le défaut d'assurance est un délit au sens de l'article L. 243-3 du code des assurances, puni d'emprisonnement et d'amende.",

        contratTitre: "Ce que vos clients vous réclameront",
        contrat: "Pour une activité qui n'est ni réglementée ni du bâtiment, aucun texte n'impose la responsabilité civile professionnelle. Vos clients, eux, la demanderont : prenez l'attestation avant le premier gros contrat plutôt qu'au moment de le signer, parce qu'un assureur met quelques jours et qu'un appel d'offres n'attend pas.",

        anglesTitre: "À savoir avant de signer, quel que soit le contrat",
        angle1: "Une attestation ne couvre que les activités déclarées à l'assureur, telles qu'elles y sont écrites. Le graphiste qui se met à revendre du matériel garde une attestation valable, mais pas la garantie qui va avec.",
        angle2: "Un client peut réclamer longtemps après la livraison, et beaucoup de contrats ne jouent que si la réclamation arrive pendant la vie du contrat, prolongée d'une période dite subséquente. Résilier en fermant son activité peut donc laisser un trou : la durée se lit dans les conditions générales, avant de signer.",
        angle3: "La prime est une charge comme une autre, et le régime micro ne la déduit pas : l'abattement forfaitaire est réputé la couvrir, qu'elle existe ou non.",
        angle4: "Comparer deux primes sur leur seul prix ne dit rien : ce sont les montants de garantie, la franchise et l'étendue géographique qui font la différence entre deux contrats au même tarif.",

        source: "Obligations relevées le 8 septembre 2026 sur la fiche des assurances du micro-entrepreneur de service-public.gouv.fr, vérifiée le 7 juillet 2026.",
    },
    en: {
        phases: ["Your activity", "Your resources", "What applies"],
        retour: "Back",
        prive: "Nothing leaves this tab: the diagnosis runs in your browser, and everything is erased when you close it.",

        oui: "Yes",
        non: "No",

        batimentTitre: "Is your activity in construction?",
        batimentAide: "Building, major renovation, extension, and anything touching the structure itself. Ten-year cover is compulsory here: going without it is a criminal offence, and it must be taken out before the work starts.",
        batimentOuiDetail: "Masonry, drywalling, electrics, plumbing, roofing, painting…",
        batimentNonDetail: "My activity does not touch construction",

        reglementeeTitre: "Is your activity a regulated one?",
        reglementeeAide: "A regulated activity is one whose access or practice is governed by its own texts: a diploma, registration with a professional body, an approval.",
        reglementeeOuiDetail: "My activity is among the regulated professions",
        reglementeeNonDetail: "No particular text governs my trade",
        reglementeeNote: "No closed list is published. Check yours in {annuaire}: that is where the administration points, and a trade missing from an incomplete list is not thereby exempt.",

        vehiculeTitre: "Do you use a vehicle for your activity?",
        vehiculeAide: "A private car counts too, as soon as it is used for business travel.",
        vehiculeOuiDetail: "Client visits, deliveries, sites, rounds",
        vehiculeNonDetail: "I work without a vehicle",

        localTitre: "Do you rent business premises?",
        localAide: "Rented premises call for their own insurance, separate from the one covering your activity.",
        localOuiDetail: "Workshop, shop, office, warehouse",
        localNonDetail: "I work from home or at my clients'",

        salariesTitre: "Do you employ anyone?",
        salariesAide: "Rare in a micro-enterprise, but the obligation exists from the first contract.",
        salariesOuiDetail: "At least one employee, even part-time",
        salariesNonDetail: "I work alone",

        resultatTitre: "What you must insure",
        resultatAucuneTitre: "No insurance is imposed on you",
        recapTitre: "The detail",
        recommencer: "Start again",

        decennaleTitre: "The ten-year cover is taken out before the site opens",
        decennale: "The date that counts is the day the site opened, not the date of the damage: it must fall within the policy's validity. A terminated policy therefore still covers sites opened while it was running, and a site opened without cover will never be caught up by a later policy. Only the works declared in the policy are covered. Finally, going without the cover is a criminal offence under article L. 243-3 of the insurance code, punishable by imprisonment and a fine.",

        contratTitre: "What your clients will expect",
        contrat: "For an activity that is neither regulated nor in construction, no text imposes professional liability insurance. Your clients will ask for it all the same: get the certificate before the first large contract rather than when you sign it, because an insurer takes a few days and a tender does not wait.",

        anglesTitre: "Worth knowing before signing, whatever the policy",
        angle1: "A certificate only covers the activities declared to the insurer, as they are written there. A designer who starts reselling equipment keeps a valid certificate, but not the cover that goes with it.",
        angle2: "A client can make a claim long after delivery, and many policies only respond if the claim arrives during the life of the contract, extended by what is called a subsequent period. Terminating when you close your business can therefore leave a gap: the length is in the general conditions, to be read before signing.",
        angle3: "The premium is a cost like any other, and the micro scheme does not deduct it: the flat-rate allowance is deemed to cover it, whether it exists or not.",
        angle4: "Comparing two premiums on price alone tells you nothing: it is the cover amounts, the excess and the geographic scope that separate two policies at the same rate.",

        source: "Obligations recorded on 8 September 2026 from the micro-entrepreneur insurance page on service-public.gouv.fr, verified on 7 July 2026.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        batiment: "Si votre activité relève du bâtiment",
        reglementee: "Si votre activité est réglementée",
        vehicule: "Si vous utilisez un véhicule",
        local: "Si vous louez un local",
        salaries: "Si vous employez des salariés",
    },
    en: {
        batiment: "Whether your activity is in construction",
        reglementee: "Whether your activity is regulated",
        vehicule: "Whether you use a vehicle",
        local: "Whether you rent premises",
        salaries: "Whether you employ anyone",
    },
};

/** L'ordre des écrans, et l'icône de chacun. */
const ECRANS = [
    { cle: "batiment", suivant: "reglementee", icone: "home-add-angle-linear" },
    { cle: "reglementee", suivant: "vehicule", icone: "diploma-verified-linear" },
    { cle: "vehicule", suivant: "local", icone: "wheel-linear" },
    { cle: "local", suivant: "salaries", icone: "shop-linear" },
    { cle: "salaries", suivant: "resultat", icone: "users-group-rounded-linear" },
];

const PHASES_PAR_ECRAN = {
    batiment: 0,
    reglementee: 0,
    vehicule: 1,
    local: 1,
    salaries: 1,
    resultat: 2,
};

const VIDE = { batiment: "", reglementee: "", vehicule: "", local: "", salaries: "" };

export default function AssurancesObligatoires({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [r, setR] = useState(VIDE);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "batiment",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const controle = useMemo(() => valider(r), [r]);
    const etat = useMemo(() => obligations(r), [r]);
    const recap = useMemo(() => recapitulatif(r), [r]);
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    const ecran = ECRANS.find((e) => e.cle === etape);
    if (ecran) {
        const repondre = (valeur) => () => {
            setR((p) => ({ ...p, [ecran.cle]: valeur }));
            aller(ecran.suivant);
        };
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={PHASES_PAR_ECRAN[etape]}
                onRetour={etape === "batiment" ? undefined : retour}
                titre={t[`${ecran.cle}Titre`]}
                aide={t[`${ecran.cle}Aide`]}
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone={ecran.icone}
                        titre={t.oui}
                        detail={t[`${ecran.cle}OuiDetail`]}
                        onClick={repondre("oui")}
                    />
                    <Choix
                        icone="close-circle-linear"
                        titre={t.non}
                        detail={t[`${ecran.cle}NonDetail`]}
                        onClick={repondre("non")}
                    />
                </div>
                {etape === "reglementee" && (
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                        {remplir(t.reglementeeNote, { annuaire: ANNUAIRE_REGLEMENTEES })}
                    </p>
                )}
                {etape === "batiment" && (
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
                )}
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat
            onRetour={retour}
            retour={t.retour}
            key={etape}
            titre={etat && etat.aucune ? t.resultatAucuneTitre : t.resultatTitre}
            phases={t.phases}
            sens={sens}
            reduit={reduit}
        >
            {!recap || !etat ? (
                <div className="mt-4">
                    <ul className="space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {etat.decennale && (
                        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
                            <div className="flex items-center gap-2.5 mb-2">
                                <SolarIcon name="danger-triangle-linear" className="text-lg shrink-0 text-amber-600" />
                                <div className="font-semibold dark:text-white leading-snug">{t.decennaleTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">{t.decennale}</p>
                        </div>
                    )}

                    {etat.rcProParContrat && (
                        <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="file-check-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">{t.contratTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.contrat}</p>
                        </div>
                    )}

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="eye-closed-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.anglesTitre}</div>
                        </div>
                        <Liste items={[t.angle1, t.angle2, t.angle3, t.angle4]} />
                    </div>

                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0">
                        {remplir(t.source, {
                            risques: RISQUES_LOCAL.length,
                            part: PART_PATRONALE_MINIMALE,
                        })}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
